import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography,
  Tab
} from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';

const EditLongDescDialog = ({ open, onClose, description, onSave }) => {
  const [activeTab, setActiveTab] = useState('1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    longDesc_en: '',
    longDesc_fi: '',
    longDesc_fr: '',
    longDesc_de: ''
  });

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'fi', label: 'Finnish' },
    { code: 'fr', label: 'French' },
    { code: 'de', label: 'German' }
  ];

  useEffect(() => {
    if (description) {
      setFormData({
        longDesc_en: description.longDesc_en || '',
        longDesc_fi: description.longDesc_fi || '',
        longDesc_fr: description.longDesc_fr || '',
        longDesc_de: description.longDesc_de || ''
      });
      setError('');
      setSuccess(false);
      setActiveTab('1');
    }
  }, [description]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    if (!description) return;

    setLoading(true);
    setError('');

    try {
      const payload = {
        SKU: description.SKU,
        longDesc_en: formData.longDesc_en || '',
        longDesc_fi: formData.longDesc_fi || '',
        longDesc_fr: formData.longDesc_fr || '',
        longDesc_de: formData.longDesc_de || ''
      };

      console.log('Sending update for SKU:', description.SKU);
      console.log('Payload:', JSON.stringify(payload));

      const response = await fetch('https://elnaf.fi/apiElnaf/api/update_long_desc.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const responseText = await response.text();
      console.log('Response text:', responseText);
      
      // Check if response is HTML (error page)
      if (responseText.includes('<!DOCTYPE') || responseText.includes('<html')) {
        console.error('Received HTML instead of JSON, likely a server error');
        setError('Server error. Please check the API endpoint.');
        setLoading(false);
        return;
      }
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse JSON:', e);
        console.error('Response was:', responseText);
        setError('Invalid response from server');
        setLoading(false);
        return;
      }

      console.log('Data received:', data);
      
      // Check for success - backend might return different messages
      if (response.ok && (data.status === 'success' || data.message?.includes('uğurla') || data.message?.includes('updated'))) {
        setSuccess(true);
        console.log('Success detected! Will close in 1.5 seconds');
        // Show success for 1.5 seconds then close
        setTimeout(() => {
          console.log('Timeout reached, calling onSave');
          if (onSave) {
            console.log('Calling onSave now');
            onSave(); // This will refresh and close
          }
        }, 1500);
      } else {
        setError(data.message || 'Failed to update description');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !success) {
      setError('');
      setSuccess(false);
      setActiveTab('1');
      onClose();
    }
  };

  if (!description) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit Long Description
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          SKU: <strong>{description.SKU}</strong>
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Description updated successfully!
            </Alert>
          )}

          <TabContext value={activeTab}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList onChange={handleTabChange}>
                {languages.map((lang, index) => (
                  <Tab key={lang.code} label={lang.label} value={String(index + 1)} />
                ))}
              </TabList>
            </Box>

            {languages.map((lang, index) => (
              <TabPanel key={lang.code} value={String(index + 1)} sx={{ px: 0 }}>
                <TextField
                  fullWidth
                  label={`Long Description (${lang.label})`}
                  name={`longDesc_${lang.code === 'fi' ? 'fi' : lang.code}`}
                  value={formData[`longDesc_${lang.code === 'fi' ? 'fi' : lang.code}`]}
                  onChange={handleChange}
                  multiline
                  rows={8}
                  variant="outlined"
                  sx={{ mt: 2 }}
                />
              </TabPanel>
            ))}
          </TabContext>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading || success}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || success}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {success ? 'Saved!' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditLongDescDialog;